import { useState, useEffect, useRef, useCallback } from "react";
import { Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";

export default function UpdateBanner() {
  const [show, setShow] = useState(false);
  const [reloading, setReloading] = useState(false);
  const registrationRef = useRef(null);
  const { palette } = useTheme();

  const showBanner = useCallback((registration) => {
    registrationRef.current = registration;
    setShow(true);
  }, []);

  useEffect(() => {
    const handleSwEvent = (event) => {
      showBanner(event.detail);
    };
    window.addEventListener("sw:update-available", handleSwEvent);

    const checkWaitingWorker = async () => {
      const reg = await navigator.serviceWorker?.getRegistration();
      if (reg?.waiting) {
        showBanner(reg);
      }
    };

    checkWaitingWorker();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkWaitingWorker();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("sw:update-available", handleSwEvent);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [showBanner]);

  const handleReload = () => {
    setReloading(true);
    const reg = registrationRef.current;

    if (reg?.waiting) {
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        () => window.location.reload(),
        { once: true }
      );
      reg.waiting.postMessage({ type: "SKIP_WAITING" });
    }
  };

  if (!show) return null;

  if (reloading) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 99999,
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={48} sx={{ color: palette.primary.main }} />
        <Typography
          sx={{
            fontSize: { xs: "4vw", sm: "1.6vw", md: "1vw" },
            fontWeight: 600,
            color: palette.text.primary,
          }}
        >
          Updating, please wait...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 99999,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: { xs: "12px", sm: "16px" },
          boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.25)",
          width: { xs: "100%", sm: 420, md: 440 },
          maxWidth: "100%",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: palette.button.gradient,
            py: { xs: 2.5, sm: 3 },
            px: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <SystemUpdateAltIcon sx={{ fontSize: { xs: 36, sm: 40 }, color: "#FFFFFF" }} />
          <Typography
            sx={{
              fontSize: { xs: "5vw", sm: "1.8vw", md: "1.15vw" },
              fontWeight: 700,
              color: "#FFFFFF",
              lineHeight: 1.3,
            }}
          >
            Update Available
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: { xs: 2.5, sm: 3 } }}>
          <Typography
            sx={{
              fontSize: { xs: "3.8vw", sm: "1.5vw", md: "0.95vw" },
              color: palette.text.secondary,
              lineHeight: 1.6,
              mb: 3,
            }}
          >
            A new version of GuestHives is available. Please update to get the latest features.
          </Typography>

          <Button
            variant="contained"
            fullWidth
            onClick={handleReload}
            sx={{
              background: `${palette.button.gradient} !important`,
              color: "#FFFFFF !important",
              fontWeight: 700,
              fontSize: { xs: "3.5vw", sm: "1.4vw", md: "0.9vw" },
              py: { xs: 1.2, sm: 1.2 },
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "0px 4px 16px rgba(226, 165, 50, 0.3)",
              "&:hover": {
                boxShadow: "0px 6px 20px rgba(226, 165, 50, 0.45)",
              },
            }}
          >
            Update Now
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
